/**
 * Pure calculation functions for the CMG simulator.
 * No Vue dependency — these are plain functions that take inputs and return outputs.
 */

/**
 * CMG 2025 formula:
 * realHourlyRate * monthlyHours * (1 - (parentSalary * effortRate / 100 / wageRef))
 */
export function computeCmgPayable({
  maxRealHourlyRate,
  monthlyHours,
  parentMonthlySalaryNet,
  effortRate,
  hourlyWageReference,
}) {
  const amount = maxRealHourlyRate * monthlyHours *
    (1 - (parentMonthlySalaryNet * effortRate / 100 / hourlyWageReference));
  return Math.max(0, amount);
}

/**
 * CMG eligible amount with handicap and partial modifiers.
 */
export function computeCmgEligibleAmount({
  cmgPayable,
  cmgNotAvailable,
  handicapToggle,
  cmgAidToggle,
  parentMonthlySalaryNet,
}) {
  if (cmgNotAvailable || (parentMonthlySalaryNet !== undefined && parentMonthlySalaryNet < 0)) {
    return 0;
  }
  return cmgPayable *
    (handicapToggle ? 1.3 : 1) *
    (cmgAidToggle === 'partial' ? 0.5 : 1);
}

/**
 * Tax credit calculation.
 * Child must be younger than 6 (childAge <= 2 in the age coding).
 */
export function computeTaxesCredit({
  grossTotal,
  socialContributionsEmployer,
  maintenanceAllowanceMonthlyDetaxLimit,
  maintenanceAllowanceMonthly,
  socialContributionsCredit,
  cmgEligibleAmount,
  otherAid,
  childAge,
  taxLimit,
}) {
  const taxMax = Math.max(
    (grossTotal +
      socialContributionsEmployer +
      Math.min(maintenanceAllowanceMonthlyDetaxLimit, maintenanceAllowanceMonthly) -
      socialContributionsCredit -
      cmgEligibleAmount -
      otherAid
    ) * 12 / 2,
    0
  );

  return parseInt(childAge) > 2 ? 0 : Math.min(taxMax, taxLimit);
}

/**
 * Amount left to pay after all aids including tax credit.
 */
export function computeAmountLeftPayable({
  totalAmount,
  socialContributionsCredit,
  cmgEligibleAmount,
  otherAid,
  taxesCredit,
}) {
  return Math.max(
    totalAmount -
      socialContributionsCredit -
      cmgEligibleAmount -
      otherAid -
      (taxesCredit / 12),
    0
  );
}

/**
 * Amount left to pay before tax credit deduction.
 */
export function computeAmountLeftPayableBeforeTaxes({
  totalAmount,
  socialContributionsCredit,
  cmgEligibleAmount,
  otherAid,
}) {
  return Math.max(
    totalAmount - socialContributionsCredit - cmgEligibleAmount - otherAid,
    0
  );
}

/**
 * Convert weekly schedule to monthly values.
 */
export function computeMonthFromWeek({
  weeklyWorkedDays,
  weeklyHours,
  yearlyWeeks,
  hasOvertime,
  weeklyOvertimeFloat,
}) {
  const monthlyWorkedDays = weeklyWorkedDays * (yearlyWeeks / 12);
  const monthlyHours = weeklyHours * (yearlyWeeks / 12);

  let monthlyOvertime = 0;
  if (hasOvertime && weeklyOvertimeFloat > 0) {
    const overtimeDaily = weeklyOvertimeFloat / weeklyWorkedDays;
    monthlyOvertime = overtimeDaily * monthlyWorkedDays;
  }

  return { monthlyWorkedDays, monthlyHours, monthlyOvertime };
}

/**
 * Convert monthly schedule to weekly values.
 */
export function computeWeekFromMonth({
  monthlyHours,
  monthlyWorkedDays,
  weeklyWorkedDays,
  monthlyOvertimeFloat,
  hasOvertime,
  yearlyWeeks,
}) {
  if (hasOvertime && monthlyOvertimeFloat > 0) {
    let weeklyOvertime = 0;
    if (weeklyWorkedDays > 0 && monthlyWorkedDays > 0) {
      weeklyOvertime = (monthlyOvertimeFloat / monthlyWorkedDays) * weeklyWorkedDays;
    }

    const normalWorkingHours = monthlyHours - monthlyOvertimeFloat;
    const weeklyHours = weeklyWorkedDays > 0 && monthlyWorkedDays > 0
      ? ((normalWorkingHours / monthlyWorkedDays * weeklyWorkedDays) + weeklyOvertime)
      : 40;

    return { weeklyHours };
  }

  const weeklyHours = monthlyHours * 12 / yearlyWeeks;
  return { weeklyHours };
}

/**
 * Gross mensualization: regular + overtime wages.
 */
export function computeGrossMensualization({
  grossMonthlyWageRegular,
  grossMonthlyOvertimeWage,
}) {
  return grossMonthlyWageRegular + grossMonthlyOvertimeWage;
}

/**
 * Net mensualization: apply classic ratio to regular, detax ratio to overtime.
 */
export function computeNetMensualization({
  grossMonthlyWageRegular,
  netRatioClassic,
  grossMonthlyOvertimeWage,
  netRatioDetax,
  hasOvertime,
}) {
  const classic = grossMonthlyWageRegular * netRatioClassic;
  const detax = hasOvertime ? (grossMonthlyOvertimeWage * netRatioDetax) : 0;
  return classic + detax;
}

/**
 * Total employer cost before aids.
 */
export function computeTotalAmount({
  grossTotal,
  socialContributionsEmployer,
  maintenanceAllowanceMonthly,
  transportAllowanceMonthly,
  mealAllowanceMonthly,
}) {
  return grossTotal +
    socialContributionsEmployer +
    maintenanceAllowanceMonthly +
    transportAllowanceMonthly +
    mealAllowanceMonthly;
}
